import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Box, Grid, Typography, makeStyles } from "@material-ui/core";

import { COLORS } from "../../../theme/colors";
import { Icon } from "../../../components/base";
import { NoDataCard } from "../../../components/parts";
import { CustomPieChart } from "../../../components/charts";
import solarPotentialImg from "../../../assets/images/icons/solar-potential.svg";
import { SwitchButton } from "../../../components/SwitchButton";
import { onlyUniqueValuesOfKeyInArray, opacityHexColor } from "../../../services";
import { MAX_BUBBLES_NUMBER } from "../../demo/hotels/steps/ReviewCalculationStep";
import { BAR_COLORS, OTHER_COLOR } from "./LocationTab";
import { PreliminaryStageSolarForecast } from "../tableHeaders";

const useStyles = makeStyles(() => ({
  chartBox: {
    height: 270,
    borderRadius: 8,
    marginTop: 20,
  },
  title: {
    padding: "10px 0 0 20px",
  },
}));

export const SolarPotentialPart = ({ data, groupKey, colors }) => {
  const classes = useStyles();
  const { clusters } = useSelector((state) => state.auth);
  const preliminaryStage = clusters[0]?.preliminary_stage;
  const withParking =
    preliminaryStage.solar_forecast === PreliminaryStageSolarForecast.parking_lots;
  const roofAndParkingSuffix = withParking ? "roof & parking" : "";
  const [showDividedSolar, setShowDividedSolar] = useState(false);
  const [eicChartData, setEicChartData] = useState([]);
  const [aepChartData, setAepChartData] = useState([]);

  const setDefaultChartData = (results) => {
    const eicChartData = [];
    const aepChartData = [];

    results.forEach((item) => {
      const name = item[groupKey].toLowerCase().replace("-", "_");
      if (showDividedSolar) {
        if (item?.total_roof_eic > 0) {
          eicChartData.push({
            name: `${item[groupKey]} roof`,
            value: item.total_roof_eic,
            color: colors[name],
            unit: "kwp",
          });
        }

        if (item?.total_parking_lots_eic > 0) {
          eicChartData.push({
            name: `${item[groupKey]} parking`,
            value: item.total_parking_lots_eic,
            color: opacityHexColor(colors[name], "B3"),
            unit: "kwp",
          });
        }

        if (item?.total_roof_aep > 0) {
          aepChartData.push({
            name: `${item[groupKey]} roof`,
            value: item.total_roof_aep,
            color: colors[name],
            unit: "kwh",
          });
        }

        if (item?.total_parking_lots_aep > 0) {
          aepChartData.push({
            name: `${item[groupKey]} parking`,
            value: item.total_parking_lots_aep,
            color: opacityHexColor(colors[name], "B3"),
            unit: "kwh",
          });
        }
      } else {
        if (item?.total_roof_eic > 0 || item?.total_parking_lots_eic > 0) {
          eicChartData.push({
            name: item[groupKey],
            value: (item?.total_roof_eic || 0) + (item?.total_parking_lots_eic || 0),
            color: colors[name],
            unit: "kwp",
            onlySuffix: withParking,
            suffix: roofAndParkingSuffix,
          });
        }

        if (item?.total_roof_aep > 0 || item?.total_parking_lots_aep > 0) {
          aepChartData.push({
            name: item[groupKey],
            value: (item?.total_roof_aep || 0) + (item?.total_parking_lots_aep || 0),
            color: colors[name],
            unit: "kwh",
            onlySuffix: withParking,
            suffix: roofAndParkingSuffix,
          });
        }
      }
    });

    return [eicChartData, aepChartData];
  };

  const setDifferentLocationChartData = (results) => {
    const eicChartData = [];
    const aepChartData = [];

    let usaRoofEic = null;
    let usaRoofAep = null;
    let usaParkingLotsEic = null;
    let usaParkingLotsAep = null;
    let euRoofEic = null;
    let euRoofAep = null;
    let euParkingLotsEic = null;
    let euParkingLotsAep = null;

    results.forEach((item) => {
      const name = item[groupKey].startsWith("_") ? "USA" : "EUROPE";

      if (name === "USA") {
        if (item?.total_roof_eic > 0) usaRoofEic += item.total_roof_eic;
        if (item?.total_parking_lots_eic > 0) usaParkingLotsEic += item.total_parking_lots_eic;
        if (item?.total_roof_aep > 0) usaRoofAep += item.total_roof_aep;
        if (item?.total_parking_lots_aep > 0) usaParkingLotsAep += item.total_parking_lots_aep;
      } else if (name === "EUROPE") {
        if (item?.total_roof_eic > 0) euRoofEic += item.total_roof_eic;
        if (item?.total_parking_lots_eic > 0) euParkingLotsEic += item.total_parking_lots_eic;
        if (item?.total_roof_aep > 0) euRoofAep += item.total_roof_aep;
        if (item?.total_parking_lots_aep > 0) euParkingLotsAep += item.total_parking_lots_aep;
      }
    });

    if (showDividedSolar) {
      if (usaRoofEic !== null) {
        eicChartData.push({
          name: "USA roof",
          value: usaRoofEic,
          color: COLORS.darks[0][70],
          unit: "kwp",
        });
      }

      if (usaParkingLotsEic !== null) {
        eicChartData.push({
          name: "USA parking",
          value: usaParkingLotsEic,
          color: COLORS.darks[0][50],
          unit: "kwp",
        });
      }

      if (euRoofEic !== null) {
        eicChartData.push({
          name: "EUROPE roof",
          value: euRoofEic,
          color: COLORS.crayola,
          unit: "kwp",
        });
      }

      if (euParkingLotsEic !== null) {
        eicChartData.push({
          name: "EUROPE parking",
          value: euParkingLotsEic,
          color: opacityHexColor(COLORS.crayola, "B3"),
          unit: "kwp",
        });
      }

      if (usaRoofAep !== null) {
        aepChartData.push({
          name: "USA roof",
          value: usaRoofAep,
          color: COLORS.darks[0][70],
          unit: "kwp",
        });
      }

      if (usaParkingLotsAep !== null) {
        aepChartData.push({
          name: "USA parking",
          value: usaParkingLotsAep,
          color: COLORS.darks[0][50],
          unit: "kwp",
        });
      }

      if (euRoofAep !== null) {
        aepChartData.push({
          name: "EUROPE roof",
          value: euRoofAep,
          color: COLORS.crayola,
          unit: "kwp",
        });
      }

      if (euParkingLotsAep !== null) {
        aepChartData.push({
          name: "EUROPE parking",
          value: euParkingLotsAep,
          color: opacityHexColor(COLORS.crayola, "B3"),
          unit: "kwp",
        });
      }
    } else {
      if (usaRoofEic !== null || usaParkingLotsEic !== null) {
        eicChartData.push({
          name: "USA",
          value: usaRoofEic + usaParkingLotsEic,
          color: COLORS.darks[0][70],
          unit: "kwp",
          onlySuffix: withParking,
          suffix: roofAndParkingSuffix,
        });
      }

      if (euRoofEic !== null || euParkingLotsEic !== null) {
        eicChartData.push({
          name: "EUROPE",
          value: euRoofEic + euParkingLotsEic,
          color: COLORS.crayola,
          unit: "kwp",
          onlySuffix: withParking,
          suffix: roofAndParkingSuffix,
        });
      }

      if (usaRoofAep !== null || usaParkingLotsAep !== null) {
        aepChartData.push({
          name: "USA",
          value: usaRoofAep + usaParkingLotsAep,
          color: COLORS.darks[0][70],
          unit: "kwp",
          onlySuffix: withParking,
          suffix: roofAndParkingSuffix,
        });
      }

      if (euRoofAep !== null || euParkingLotsAep !== null) {
        aepChartData.push({
          name: "EUROPE",
          value: euRoofAep + euParkingLotsAep,
          color: COLORS.crayola,
          unit: "kwp",
          onlySuffix: withParking,
          suffix: roofAndParkingSuffix,
        });
      }
    }

    return [eicChartData, aepChartData];
  };

  const setLocationChartData = (results) => {
    const eicChartData = [];
    const aepChartData = [];

    const mainResults = results.slice(0, MAX_BUBBLES_NUMBER - 1);
    const otherResults = results.slice(MAX_BUBBLES_NUMBER - 1);

    mainResults.forEach((item, i) => {
      const name = item.location.toUpperCase().replace("_", "");
      item.location = name;

      if (showDividedSolar) {
        if (item?.total_roof_eic > 0) {
          eicChartData.push({
            name: `${name} roof`,
            value: item?.total_roof_eic,
            color: BAR_COLORS[i],
            unit: "kwp",
          });
        }

        if (item?.total_parking_lots_eic > 0) {
          eicChartData.push({
            name: `${name} parking`,
            value: item?.total_parking_lots_eic,
            color: opacityHexColor(BAR_COLORS[i], "B3"),
            unit: "kwp",
          });
        }

        if (item?.total_roof_aep > 0) {
          aepChartData.push({
            name: `${name} roof`,
            value: item?.total_roof_aep,
            color: BAR_COLORS[i],
            unit: "kwp",
          });
        }

        if (item?.total_parking_lots_aep > 0) {
          aepChartData.push({
            name: `${name} parking`,
            value: item?.total_parking_lots_aep,
            color: opacityHexColor(BAR_COLORS[i], "B3"),
            unit: "kwp",
          });
        }
      } else {
        if (item?.total_roof_eic > 0 || item?.total_parking_lots_eic > 0) {
          eicChartData.push({
            name: name,
            value: (item?.total_roof_eic || 0) + (item?.total_parking_lots_eic || 0),
            color: BAR_COLORS[i],
            unit: "kwp",
            onlySuffix: withParking,
            suffix: roofAndParkingSuffix,
          });
        }

        if (item?.total_roof_aep > 0 || item?.total_parking_lots_aep > 0) {
          aepChartData.push({
            name: name,
            value: (item?.total_roof_aep || 0) + (item?.total_parking_lots_aep || 0),
            color: BAR_COLORS[i],
            unit: "kwp",
            onlySuffix: withParking,
            suffix: roofAndParkingSuffix,
          });
        }
      }
    });

    if (otherResults.length) {
      const eicRoofData = {
        name: "Others roof",
        value: 0,
        color: OTHER_COLOR,
        unit: "kwp",
      };
      const eicParkingLotsData = {
        name: "Others parking",
        value: 0,
        color: opacityHexColor(OTHER_COLOR, "B3"),
        unit: "kwp",
      };

      const aepRoofData = {
        name: "Others roof",
        value: 0,
        color: OTHER_COLOR,
        unit: "kwp",
      };
      const aepParkingLotsData = {
        name: "Others parking",
        value: 0,
        color: opacityHexColor(OTHER_COLOR, "B3"),
        unit: "kwp",
      };

      otherResults.forEach((item) => {
        if (item?.total_roof_eic > 0) eicRoofData.value += item.total_roof_eic;
        if (item?.total_parking_lots_eic > 0)
          eicParkingLotsData.value += item.total_parking_lots_eic;
        if (item?.total_roof_aep > 0) aepRoofData.value += item.total_roof_aep;
        if (item?.total_parking_lots_aep > 0)
          aepParkingLotsData.value += item.total_parking_lots_aep;
      });

      if (showDividedSolar) {
        eicChartData.push(eicRoofData, eicParkingLotsData);
        aepChartData.push(aepRoofData, aepParkingLotsData);
      } else {
        eicChartData.push({
          name: "Others",
          value: eicRoofData.value + eicParkingLotsData.value,
          color: OTHER_COLOR,
          unit: "kwp",
          onlySuffix: withParking,
          suffix: roofAndParkingSuffix,
        });
        aepChartData.push({
          name: "Others",
          value: aepRoofData.value + aepParkingLotsData.value,
          color: OTHER_COLOR,
          unit: "kwp",
          onlySuffix: withParking,
          suffix: roofAndParkingSuffix,
        });
      }
    }

    return [eicChartData, aepChartData];
  };

  useEffect(() => {
    const results = groupKey === "location" ? data.results : data;
    let eicChartData = [];
    let aepChartData = [];

    if (preliminaryStage.solar_forecast !== PreliminaryStageSolarForecast.exclude) {
      if (groupKey !== "location") {
        [eicChartData, aepChartData] = setDefaultChartData(results);
      } else if (groupKey === "location" && data.usa_state_count && data.europe_country_count) {
        [eicChartData, aepChartData] = setDifferentLocationChartData(results);
      } else if (groupKey === "location") {
        [eicChartData, aepChartData] = setLocationChartData(results);
      }
    }

    if (!onlyUniqueValuesOfKeyInArray(eicChartData, "value", null)) {
      setEicChartData(eicChartData);
    }
    if (!onlyUniqueValuesOfKeyInArray(aepChartData, "value", null)) {
      setAepChartData(aepChartData);
    }
  }, [showDividedSolar]);

  return (
    <>
      {!eicChartData.length && !aepChartData.length ? null : (
        <Box style={{ backgroundColor: COLORS.darks[0][3], borderRadius: 8 }}>
          <Grid container>
            <Grid
              item
              xs={4}
              container
              justifyContent="center"
              alignItems="center"
              direction="column"
              style={{ position: "relative" }}
            >
              <Icon src={solarPotentialImg} />
              <Typography variant="h2" style={{ fontWeight: 500, marginTop: 20 }}>
                Solar Potential
              </Typography>
              {withParking ? (
                <Box style={{ position: "absolute", bottom: 10, left: 10 }}>
                  <SwitchButton
                    checked={showDividedSolar}
                    handleChange={(e) => setShowDividedSolar(e.target.checked)}
                    label="Break by roof/parking lot"
                  />
                </Box>
              ) : null}
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h4" className={classes.title}>
                Est. Installed capacity (kWp)
              </Typography>
              <Box className={classes.chartBox}>
                {eicChartData.length ? (
                  <CustomPieChart name="SolarPowerConsumptionKwp" data={eicChartData} showTooltip />
                ) : (
                  <NoDataCard withShaDowBox={false} gridStyle={{ height: 160 }} />
                )}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="h4" className={classes.title}>
                Est. Annual production (kWh/Year)
              </Typography>
              <Box className={classes.chartBox}>
                {aepChartData.length ? (
                  <CustomPieChart
                    name="SolarPowerConsumptionPerYearKwh"
                    data={aepChartData}
                    showTooltip
                  />
                ) : (
                  <NoDataCard withShaDowBox={false} gridStyle={{ height: 160 }} />
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};
